import { isDevelopment } from '@/lib/config';
import {
  ActorRole,
  DealItemWithVersions,
  LanguageIso6391,
} from '@/lib/graphql/generated/graphql';
import { CurrencyCode } from '@/types/currency';
import { Locale } from '@/types/locale';
import { Store } from '@/types/store';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const toBase64Url = (value: string) => {
  const binary = Array.from(new TextEncoder().encode(value), (byte) =>
    String.fromCharCode(byte)
  ).join('');
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * Resize a public image URL using imgproxy
 * @param image - The source image URL (must be a valid URL), sent base64url-encoded
 * @param width - Desired width in pixels
 * @param height - Desired height in pixels
 * @returns The imgproxy URL for the resized image, or the original URL if invalid
 */
export const resizePublicImage = (
  image: string,
  width: number,
  height: number
) => {
  if (!image || typeof image !== 'string' || image.trim() === '') {
    console.log('[imgproxy] Invalid image URL (empty):', image);
    return image;
  }

  try {
    const url = new URL(image);
    if (!['http:', 'https:'].includes(url.protocol)) {
      console.log('[imgproxy] Invalid protocol:', url.protocol, image);
      return image;
    }
  } catch {
    console.log('[imgproxy] URL parsing failed:', image);
    return image;
  }

  // Safely get devicePixelRatio (defaults to 1 for SSR or if undefined)
  const pixelRatio =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;

  // Calculate actual dimensions with pixel ratio
  const actualWidth = Math.max(1, Math.round(width * pixelRatio));
  const actualHeight = Math.max(1, Math.round(height * pixelRatio));

  // A plain source loses its query and breaks on `@`, `%` or a nested imgproxy URL: imgproxy takes it base64url-encoded
  return `https://images${isDevelopment ? '-dev' : ''}.choose.app/resize/insecure/rs:fill:${actualWidth}:${actualHeight}/${toBase64Url(image)}`;
};

export const hasCriticalErrors = (
  validationErrors: Array<{ severity: string }> | null | undefined
): boolean => {
  if (!validationErrors || !Array.isArray(validationErrors)) return false;
  return validationErrors.some((error) => error.severity === 'error');
};

export type ComparedField =
  | 'stockQuantity'
  | 'chooseDiscount'
  | 'shippingPrice'
  | 'choosePrice';

export const hasPreviousVersion = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole
): boolean => {
  const previousVersion =
    role === ActorRole.Brand
      ? product.lastBrandVersion
      : product.lastChooseVersion;

  return !!previousVersion && typeof previousVersion[field] !== 'undefined';
};

export const getMyLastValue = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole,
  defaultValue: number = 0
): number => {
  const previousVersion =
    role === ActorRole.Brand
      ? product.lastBrandVersion
      : product.lastChooseVersion;

  if (!previousVersion) return defaultValue;

  return previousVersion[field] ?? defaultValue;
};

export const getTheirLastValue = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole,
  defaultValue: number = 0
): number => {
  const previousVersion =
    role === ActorRole.Brand
      ? product.lastChooseVersion
      : product.lastBrandVersion;

  if (!previousVersion) return defaultValue;

  return previousVersion[field] ?? defaultValue;
};

export const getCurrentValue = (
  product: DealItemWithVersions,
  field: ComparedField,
  defaultValue: number = 0
): number => {
  return product.activeVersion[field] ?? defaultValue;
};

export const getDeltaValue = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole
): number => {
  const current = getCurrentValue(product, field);
  const previous = getMyLastValue(product, field, role);
  return current - previous;
};

export const getDeltaSign = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole
): number => {
  return Math.sign(getDeltaValue(product, field, role));
};

export const isFieldModified = (
  product: DealItemWithVersions,
  field: ComparedField,
  role: ActorRole,
  editionMode: ActorRole | null
): boolean => {
  if (!hasPreviousVersion(product, field, role)) return false;

  const valueToCompare =
    editionMode === role
      ? getTheirLastValue(product, field, role)
      : getCurrentValue(product, field);
  return getMyLastValue(product, field, role) !== valueToCompare;
};

/**
 * Détecte si un produit a été modifié en comparant les versions
 * @param product - Le produit à vérifier
 * @param initialMode - Le mode initial (BRAND ou CHOOSE)
 * @returns true si le produit a été modifié
 */
export const isProductModified = (
  product: DealItemWithVersions,
  role: ActorRole,
  editionMode: ActorRole | null
): boolean => {
  const stockModified = isFieldModified(
    product,
    'stockQuantity',
    role,
    editionMode
  );
  const discountModified = isFieldModified(
    product,
    'chooseDiscount',
    role,
    editionMode
  );
  const shippingPriceModified = isFieldModified(
    product,
    'shippingPrice',
    role,
    editionMode
  );
  const choosePriceModified = isFieldModified(
    product,
    'choosePrice',
    role,
    editionMode
  );

  return (
    stockModified ||
    discountModified ||
    shippingPriceModified ||
    choosePriceModified
  );
};

export const getModifierName = (
  role: ActorRole,
  translatedBrandRole: string
): string => {
  return role === ActorRole.Brand ? 'Choose' : translatedBrandRole;
};

/**
 * Maps i18n language code to LanguageIso6391 enum value
 * @param language - i18n language code (e.g., 'en', 'fr')
 * @returns LanguageIso6391 enum value (e.g., 'en_US', 'fr_FR')
 */
export const mapI18nLanguageToLanguageIso6391 = (
  language: string
): LanguageIso6391 => {
  // Normalize language code (handle 'en-US', 'fr-FR', 'en', 'fr', etc.)
  const normalizedLang = language.toLowerCase().split('-')[0];

  switch (normalizedLang) {
    case 'en':
      return LanguageIso6391.EnUs;
    case 'fr':
      return LanguageIso6391.FrFr;
    default:
      // Default to French if language is not recognized
      return LanguageIso6391.FrFr;
  }
};

const numberFormatters = new Map<string, Intl.NumberFormat>();

const getNumberFormatter = (
  locale: string,
  currency: CurrencyCode
): Intl.NumberFormat => {
  const key = `${locale}-${currency}`;
  let formatter = numberFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    numberFormatters.set(key, formatter);
  }

  return formatter;
};

export const formatCurrency = (
  value: number | null | undefined,
  store: Store
): string => {
  if (value == null) return '';

  const currency = store === Store.FR ? CurrencyCode.EUR : CurrencyCode.USD;
  const locale = store === Store.FR ? Locale.FR : Locale.US;

  return getNumberFormatter(locale, currency).format(value);
};

/** Clé i18n pour le libellé TVA selon le store (FR = incl. VAT, US = excl. VAT). À utiliser avec t(). */
export function getVatLabelKey(store: Store): 'kpi.incl_vat' | 'kpi.excl_vat' {
  return store === Store.FR ? 'kpi.incl_vat' : 'kpi.excl_vat';
}

/**
 * Formats a number with 2 decimal places using comma as decimal separator
 * This is the standard format used throughout the application for percentages and decimal values
 * @param value - The number to format
 * @returns Formatted string with 2 decimal places and comma separator (e.g., "12,34")
 */
export const formatDecimal = (value: number | null | undefined): string => {
  if (value == null) return '';
  return value.toFixed(2).replace('.', ',');
};
